import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, UploadCloud, Loader2, Video } from 'lucide-react';
import { toast } from '@clareefai/ui';
import { uploadFile } from '../../utils/upload';
import { useGetPromotersQuery } from '../../store/promoterApi';
import { useCreateTestimonialRequestMutation } from '../../store/vendorApi';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** When set, the promoter is pre-selected and the picker is hidden. */
  promoterId?: string;
  promoterName?: string;
};

const ALLOWED_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_SIZE_BYTES = 500 * 1024 * 1024; // 500 MB — mirrors the promoter VideoTestimonialPage limit

const UploadPromoterVideoModal: React.FC<Props> = ({ isOpen, onClose, promoterId, promoterName }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Only fetch the promoter list when we need the picker.
  const { data: promoters = [] } = useGetPromotersQuery(undefined, { skip: !!promoterId });
  const [createRequest] = useCreateTestimonialRequestMutation();

  const [selectedPromoterId, setSelectedPromoterId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const effectivePromoterId = promoterId || selectedPromoterId;

  const reset = () => {
    setSelectedPromoterId('');
    setFile(null);
    setTitle('');
    setDescription('');
    setTags('');
    setProgress(0);
    setError(null);
  };

  const handleClose = () => {
    if (isUploading) return;
    reset();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const f = e.target.files?.[0] ?? null;
    if (!f) {
      setFile(null);
      return;
    }
    if (!ALLOWED_TYPES.includes(f.type)) {
      setError(t('testimonials.uploadVideo.errorFileType'));
      setFile(null);
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      setError(t('testimonials.uploadVideo.errorFileSize'));
      setFile(null);
      return;
    }
    setFile(f);
  };

  const handleInvite = () => {
    reset();
    onClose();
    navigate('/add-promoter');
  };

  const handleSubmit = async () => {
    setError(null);
    if (!effectivePromoterId) {
      setError(t('testimonials.uploadVideo.errorNoPromoter'));
      return;
    }
    if (!file) {
      setError(t('testimonials.uploadVideo.errorNoFile'));
      return;
    }
    if (!title.trim()) {
      setError(t('testimonials.uploadVideo.errorNoTitle'));
      return;
    }

    setIsUploading(true);
    setProgress(0);
    try {
      const videoUrl = await uploadFile(file, 'testimonials', setProgress);
      const videoTags = tags
        .split(',')
        .map((tg) => tg.trim())
        .filter(Boolean);
      await createRequest({
        promoterId: effectivePromoterId,
        type: 'VIDEO',
        video_url: videoUrl,
        video_title: title.trim(),
        video_description: description.trim() || undefined,
        video_tags: videoTags.length ? videoTags : undefined,
      }).unwrap();
      toast.success(t('testimonials.uploadVideo.successToast'));
      reset();
      onClose();
    } catch (err) {
      console.error('Failed to upload promoter video:', err);
      setError(t('testimonials.uploadVideo.errorToast'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between border-b border-gray-200 p-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Video className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('testimonials.uploadVideo.title')}</h3>
              <p className="text-sm text-gray-600 mt-0.5">{t('testimonials.uploadVideo.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isUploading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Promoter picker (hidden when pre-selected) */}
          {promoterId ? (
            promoterName && (
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-1">
                  {t('testimonials.uploadVideo.selectPromoter')}
                </span>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900">
                  {promoterName}
                </div>
              </div>
            )
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('testimonials.uploadVideo.selectPromoter')}
              </label>
              {promoters.length === 0 ? (
                <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="mb-2">{t('testimonials.uploadVideo.noPromoters')}</p>
                  <button
                    type="button"
                    onClick={handleInvite}
                    className="text-purple-600 hover:text-purple-800 font-medium"
                  >
                    {t('testimonials.uploadVideo.invitePromoter')}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <select
                    value={selectedPromoterId}
                    onChange={(e) => setSelectedPromoterId(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  >
                    <option value="">{t('testimonials.uploadVideo.selectPromoterPlaceholder')}</option>
                    {promoters.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.firstName} {p.lastName}
                        {p.company ? ` — ${p.company}` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleInvite}
                    className="whitespace-nowrap text-sm text-purple-600 hover:text-purple-800 font-medium"
                  >
                    {t('testimonials.uploadVideo.invitePromoter')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Video file */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('testimonials.uploadVideo.videoFile')}
            </label>
            <label className="flex flex-col items-center justify-center w-full border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-purple-400 transition-colors">
              <UploadCloud className="w-6 h-6 text-gray-400 mb-1" />
              <span className="text-sm text-gray-700">
                {file ? file.name : t('testimonials.uploadVideo.chooseFile')}
              </span>
              <span className="text-xs text-gray-400 mt-0.5">{t('testimonials.uploadVideo.fileHint')}</span>
              <input
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={handleFileChange}
                disabled={isUploading}
                className="hidden"
              />
            </label>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('testimonials.uploadVideo.videoTitle')}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('testimonials.uploadVideo.videoTitlePlaceholder')}
              disabled={isUploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('testimonials.uploadVideo.videoDescription')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('testimonials.uploadVideo.videoDescriptionPlaceholder')}
              rows={3}
              disabled={isUploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('testimonials.uploadVideo.videoTags')}
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder={t('testimonials.uploadVideo.videoTagsPlaceholder')}
              disabled={isUploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            />
          </div>

          {isUploading && (
            <div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {t('testimonials.uploadVideo.uploading', { progress })}
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 border-t border-gray-200 p-5">
          <button
            onClick={handleClose}
            disabled={isUploading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            {t('testimonials.uploadVideo.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isUploading}
            className="flex items-center px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('testimonials.uploadVideo.submitting')}
              </>
            ) : (
              t('testimonials.uploadVideo.submit')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadPromoterVideoModal;
